import React, { useEffect, useState, useRef, createContext, useContext } from 'react';
import { Provider } from 'react-redux';
import { createHashHistory, createBrowserHistory } from 'history';
import { logger, useAuth, AuthProvider } from '@iq/react-components';
import { HvProvider, HvTypography } from '@hitachivantara/uikit-react-core';

import config from '../../config';
import setupStore from '../../store';
import { getConfiguration } from '../../services';

import Router from '../Router';
import GlobalMessagePortal from '../Notifications/GlobalMessagePortal';
import TopNotification from '../Notifications/TopNotification';

import { AppContextProvider, initialState } from '../../AppContext';
import { ObservableStateProvider, checkNetworkStatus, getRealm } from '../../utils';

const store = setupStore();
const authConfig = { ...config.auth };

const authInitConfig = {
  pkceMethod: 'S256',
  enableLogging: true,
};

const history =
  globalThis.navigator && globalThis.navigator.standalone
    ? createHashHistory()
    : createBrowserHistory();

// Client config context for sharing fetched config (mapboxAccessToken, etc.)
const ClientConfigContext = createContext({});
export const useClientConfig = () => useContext(ClientConfigContext);

const App = () => {
  const [online, setOnline] = useState(true);
  const [clientConfig, setClientConfig] = useState({});
  const configFetched = useRef(false);
  const loggerInitialized = useRef(false);
  const { authenticated, subject, logOut, authError, initialize } = useAuth();

  const checkStatus = async () => {
    if (navigator.onLine) {
      const status = await checkNetworkStatus();
      setOnline(status);
    } else {
      setOnline(false);
    }
  };

  // Fetch client config (mapbox token, appInsights) from auth service after authentication
  useEffect(() => {
    if (authenticated && !configFetched.current) {
      configFetched.current = true;
      getConfiguration()
        .then((fetchedConfig) => {
          setClientConfig(fetchedConfig);

          const { applicationInsights } = fetchedConfig;
          if (applicationInsights && !loggerInitialized.current) {
            const { connectionString, maxAjaxCallsPerView, ...rest } = applicationInsights;
            logger.init(
              {
                ...rest,
                connectionString,
                maxAjaxCallsPerView,
              },
              { browserHistory: history }
            );
            loggerInitialized.current = true;
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch client config:', error);
        });
    }
  }, [authenticated]);

  useEffect(() => {
    let checkStatusInterval;
    if (online) {
      initialize();

      if (checkStatusInterval) {
        clearInterval(checkStatusInterval);
      }
    } else {
      checkStatusInterval = setInterval(checkStatus, 5000);
    }
    return () => {
      if (checkStatusInterval) {
        clearInterval(checkStatusInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  useEffect(() => {
    if (authenticated) {
      logger.setUser(subject);
    } else {
      logger.setUser(null);
    }
  }, [authenticated, subject]);

  useEffect(() => {
    if (authError) {
      checkStatus();
    }
  }, [authError]);

  if (!authenticated && !authError) {
    return null;
  }

  if (!authenticated && authError && online) {
    return (
      <GlobalMessagePortal>
        <TopNotification
          message="Unable to authenticate"
          action={logOut}
          actionText="Retry login"
        />
      </GlobalMessagePortal>
    );
  }

  return (
    <ClientConfigContext.Provider value={clientConfig}>
      <Provider store={store}>
        <ObservableStateProvider>
          <AppContextProvider value={initialState}>
            <GlobalMessagePortal>
              {!online && (
                <TopNotification
                  icon="he-nonetwork"
                  message="No internet connection"
                />
              )}
            </GlobalMessagePortal>
            <Router history={history} />
          </AppContextProvider>
        </ObservableStateProvider>
      </Provider>
    </ClientConfigContext.Provider>
  );
};

const AppWithProviders = (properties) => {
  const realm = getRealm();
  return (
    <AuthProvider
      config={{ ...authConfig, realm }}
      initConfig={authInitConfig}
    >
      <HvProvider>
        <App {...properties} />
      </HvProvider>
    </AuthProvider>
  );
};

export default AppWithProviders;
